import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  message,
  Space,
  Popconfirm,
  Tag,
  DatePicker,
  Select,
  Card,
  Upload,
  Pagination,
  Tabs,
  Statistic,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import dayjs from 'dayjs';
import { compressFile } from '../../utils/fileCompression';
import { LoginContext } from '../../context/loginContext';

// Función para calcular la edad a partir de la fecha de nacimiento
const calcularEdad = (fechaNacimiento) => {
  if (!fechaNacimiento) return null;
  
  const fechaNac = dayjs(fechaNacimiento);
  const hoy = dayjs();
  const edad = hoy.diff(fechaNac, 'year');
  
  return edad >= 0 ? edad : null;
};

const AdministracionAlumnos = () => {
  const { user } = React.useContext(LoginContext);
  const [alumnos, setAlumnos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [tiposPago, setTiposPago] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalCreditosVisible, setModalCreditosVisible] = useState(false);
  const [editingAlumno, setEditingAlumno] = useState(null);
  const [alumnoParaCreditos, setAlumnoParaCreditos] = useState(null);
  const [form] = Form.useForm();
  const [formCreditos] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [comprobanteFile, setComprobanteFile] = useState(null);
  const [filtroNombre, setFiltroNombre] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [creditosAlumno, setCreditosAlumno] = useState([]);
  const [loadingCreditos, setLoadingCreditos] = useState(false);
  const [paginationCreditos, setPaginationCreditos] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [totalCreditosActivos, setTotalCreditosActivos] = useState(0);
  const [activeTab, setActiveTab] = useState('cargar');
  const [creditoGratuito, setCreditoGratuito] = useState(false);

  // Verificar si el usuario es administrador
  const esAdministrador = () => {
    if (!user) {
      console.log('❌ esAdministrador: No hay usuario');
      return false;
    }
    
    // El objeto user puede tener diferentes estructuras:
    // - Del login: usu_tipousuario, tiu_descripcion
    // - Del JWT: tipoUsuario
    const userTipoUsuario = user?.tipoUsuario || user?.usu_tipousuario;
    const userRole = user?.tiu_descripcion || '';
    
    // Verificar por tipoUsuario (1 = ADMINISTRADOR)
    if (userTipoUsuario !== undefined && userTipoUsuario !== null) {
      if (userTipoUsuario === 1 || userTipoUsuario === '1') {
        console.log('✅ esAdministrador: Rol detectado por tipoUsuario = 1');
        return true;
      }
    }
    
    // Verificar por rol de texto
    if (userRole && userRole.toUpperCase() === 'ADMINISTRADOR') {
      console.log('✅ esAdministrador: Rol detectado por tiu_descripcion = ADMINISTRADOR');
      return true;
    }
    
    console.log('❌ esAdministrador: No es administrador', {
      userTipoUsuario,
      userRole,
      user
    });
    return false;
  };

  // Cargar categorías
  const loadCategorias = async () => {
    try {
      const response = await apiClient.get('/categorias');
      if (response.ok) {
        const data = await response.json();
        setCategorias(data.filter(cat => cat.cat_activo));
      }
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    }
  };

  // Cargar planes
  const loadPlanes = async () => {
    try {
      const response = await apiClient.get('/planes');
      if (response.ok) {
        const data = await response.json();
        // Filtrar planes activos y excluir el plan "Crédito Gratuito" (ya que hay un switch para eso)
        const planesFiltrados = data.filter(plan => {
          const esActivo = plan.pla_activo;
          const nombrePlan = (plan.pla_nombre || '').toLowerCase();
          const esGratuito = nombrePlan.includes('gratuito') || nombrePlan.includes('gratis');
          return esActivo && !esGratuito;
        });
        setPlanes(planesFiltrados);
      }
    } catch (error) {
      console.error('Error al cargar planes:', error);
    }
  };

  // Cargar tipos de pago
  const loadTiposPago = async () => {
    try {
      const response = await apiClient.get('/tipo-pagos');
      if (response.ok) {
        const data = await response.json();
        setTiposPago(data);
      }
    } catch (error) {
      console.error('Error al cargar tipos de pago:', error);
    }
  };

  // Cargar alumnos con filtros y paginación
  const loadAlumnos = async (nombre = null, categoriaId = null, page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (nombre) params.append('nombre', nombre);
      if (categoriaId) params.append('categoriaId', categoriaId);
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());
      
      const queryString = params.toString();
      const url = `/alumnos?${queryString}`;
      
      const response = await apiClient.get(url);
      if (response.ok) {
        const result = await response.json();
        setAlumnos(result.data || []);
        setPagination({
          current: result.page || 1,
          pageSize: result.pageSize || 10,
          total: result.total || 0,
        });
      } else {
        message.error('Error al cargar los alumnos');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los alumnos');
    } finally {
      setLoading(false);
    }
  };

  // Manejar cambio en filtro de nombre
  const handleFiltroNombreChange = (e) => {
    const valor = e.target.value;
    setFiltroNombre(valor);
    // Debounce: esperar 500ms después de que el usuario deje de escribir
    clearTimeout(window.filtroNombreTimeout);
    window.filtroNombreTimeout = setTimeout(() => {
      setPagination(prev => ({ ...prev, current: 1 }));
      loadAlumnos(valor || null, filtroCategoria, 1, pagination.pageSize);
    }, 500);
  };

  // Manejar cambio en filtro de categoría
  const handleFiltroCategoriaChange = (value) => {
    setFiltroCategoria(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    loadAlumnos(filtroNombre || null, value || null, 1, pagination.pageSize);
  };

  // Limpiar filtros
  const handleLimpiarFiltros = () => {
    setFiltroNombre('');
    setFiltroCategoria(null);
    setPagination(prev => ({ ...prev, current: 1 }));
    loadAlumnos(null, null, 1, pagination.pageSize);
  };

  // Manejar cambio de página
  const handleTableChange = (newPagination) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total,
    });
    loadAlumnos(
      filtroNombre || null,
      filtroCategoria,
      newPagination.current,
      newPagination.pageSize
    );
  };

  useEffect(() => {
    loadCategorias();
    loadPlanes();
    loadTiposPago();
    loadAlumnos(null, null, 1, 10);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Abrir modal para crear nuevo alumno
  const handleCreate = () => {
    setEditingAlumno(null);
    form.resetFields();
    form.setFieldsValue({ activo: true });
    setModalVisible(true);
  };

  // Abrir modal para editar alumno
  const handleEdit = (record) => {
    setEditingAlumno(record);
    form.setFieldsValue({
      nombre: record.alu_nombre,
      apellido: record.alu_apellido,
      fechaNacimiento: record.alu_fecha_nacimiento ? dayjs(record.alu_fecha_nacimiento) : null,
      sexo: record.alu_sexo,
      email: record.alu_email,
      telefono: record.alu_telefono,
      categoria: record.alu_categoria,
      activo: record.alu_activo,
    });
    setModalVisible(true);
  };

  // Guardar alumno (crear o actualizar)
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      // Convertir fecha de dayjs a string formato YYYY-MM-DD
      const dataToSend = {
        ...values,
        fechaNacimiento: values.fechaNacimiento 
          ? values.fechaNacimiento.format('YYYY-MM-DD')
          : null,
      };
      
      if (editingAlumno) {
        // Actualizar
        const response = await apiClient.put(`/alumnos/${editingAlumno.alu_id}`, dataToSend);
        if (response.ok) {
          message.success('Alumno actualizado correctamente');
          setModalVisible(false);
          loadAlumnos(filtroNombre || null, filtroCategoria, pagination.current, pagination.pageSize);
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al actualizar el alumno');
        }
      } else {
        // Crear
        const response = await apiClient.post('/alumnos', dataToSend);
        if (response.ok) {
          message.success('Alumno creado correctamente');
          setModalVisible(false);
          loadAlumnos(filtroNombre || null, filtroCategoria, pagination.current, pagination.pageSize);
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al crear el alumno');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.errorFields) {
        // Errores de validación del formulario
        return;
      }
      message.error('Error al guardar el alumno');
    }
  };

  // Eliminar alumno
  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/alumnos/${id}`);
      if (response.ok) {
        message.success('Alumno eliminado correctamente');
        loadAlumnos(filtroNombre || null, filtroCategoria, pagination.current, pagination.pageSize);
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar el alumno');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar el alumno');
    }
  };

  // Cargar créditos del alumno
  const loadCreditosAlumno = async (alumnoId, page = 1, pageSize = 10) => {
    setLoadingCreditos(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());
      
      const url = `/creditos/alumno/${alumnoId}?${params.toString()}`;
      const response = await apiClient.get(url);
      
      if (response.ok) {
        const result = await response.json();
        setCreditosAlumno(result.data || []);
        setPaginationCreditos({
          current: result.page || 1,
          pageSize: result.pageSize || 10,
          total: result.total || 0,
        });
        setTotalCreditosActivos(result.totalActivos || 0);
      } else {
        message.error('Error al cargar los créditos');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los créditos');
    } finally {
      setLoadingCreditos(false);
    }
  };

  // Abrir modal para cargar créditos
  const handleCargarCreditos = (record) => {
    setAlumnoParaCreditos(record);
    formCreditos.resetFields();
    setComprobanteFile(null);
    setCreditoGratuito(false);
    setActiveTab('cargar');
    setModalCreditosVisible(true);
    // Cargar créditos cuando se abre el modal
    loadCreditosAlumno(record.alu_id, 1, 10);
  };

  // Manejar cambio de pestaña
  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === 'ver' && alumnoParaCreditos) {
      // Cargar créditos cuando se cambia a la pestaña de ver
      loadCreditosAlumno(alumnoParaCreditos.alu_id, paginationCreditos.current, paginationCreditos.pageSize);
    }
  };

  // Manejar cambio de página en créditos
  const handleCreditosTableChange = (newPagination) => {
    setPaginationCreditos({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: paginationCreditos.total,
    });
    if (alumnoParaCreditos) {
      loadCreditosAlumno(
        alumnoParaCreditos.alu_id,
        newPagination.current,
        newPagination.pageSize
      );
    }
  };

  // Convertir archivo a base64 (después de comprimir)
  const getBase64 = async (file) => {
    try {
      // Comprimir el archivo primero
      const compressedFile = await compressFile(file);
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(compressedFile);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    } catch (error) {
      message.error(error.message || 'Error al procesar el archivo');
      throw error;
    }
  };

  // Guardar créditos
  const handleSaveCreditos = async () => {
    try {
      // Si es crédito gratuito, validar que el usuario sea administrador ANTES de validar campos
      if (creditoGratuito) {
        const esAdmin = esAdministrador();
        console.log('🔍 Verificando administrador para crédito gratuito:', {
          creditoGratuito,
          esAdmin,
          user
        });
        
        if (!esAdmin) {
          message.error('Solo los administradores pueden cargar créditos gratuitos');
          return;
        }
      }
      
      // Si es crédito gratuito, validar solo los campos necesarios
      let values;
      if (creditoGratuito) {
        // Validar solo el campo de días de validez
        try {
          await formCreditos.validateFields(['diasValidezGratuito']);
          values = formCreditos.getFieldsValue();
        } catch (error) {
          // Si hay errores de validación, mostrarlos
          if (error.errorFields) {
            return;
          }
          throw error;
        }
      } else {
        // Validar todos los campos normalmente
        values = await formCreditos.validateFields();
      }

      let comprobanteBase64 = null;
      let tipoPagoId = null;
      const metodoPago = values.metodoPago;

      // Si es crédito gratuito, no requiere método de pago
      if (creditoGratuito) {
        const dataToSend = {
          alumnoId: alumnoParaCreditos.alu_id,
          creditoGratuito: true,
          cantidadCreditos: 1,
          diasValidez: values.diasValidezGratuito || 30, // Por defecto 30 días
        };

        const response = await apiClient.post('/registro-pagos/credito-gratuito', dataToSend);
        if (response.ok) {
          const result = await response.json();
          message.success(`1 crédito gratuito cargado correctamente al alumno.`);
          formCreditos.resetFields();
          setCreditoGratuito(false);
          // Recargar créditos y cambiar a la pestaña de ver
          if (alumnoParaCreditos) {
            await loadCreditosAlumno(alumnoParaCreditos.alu_id, 1, paginationCreditos.pageSize);
            setActiveTab('ver');
          }
          loadAlumnos(filtroNombre || null, filtroCategoria, pagination.current, pagination.pageSize);
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al cargar el crédito gratuito');
        }
        return;
      }

      // Si el método de pago es PENDIENTE, no se envía tipoPagoId
      // Si es EFECTIVO o TRANSFERENCIA, se envía el tipoPagoId
      if (metodoPago !== 'PENDIENTE') {
        // Obtener el tipoPagoId según el método seleccionado
        const tipoPago = tiposPago.find(tp => tp.tip_nombre === metodoPago);
        if (tipoPago) {
          tipoPagoId = tipoPago.tip_id;
        }
        
        // Si es TRANSFERENCIA, validar que haya comprobante
        if (metodoPago === 'TRANSFERENCIA') {
          if (!comprobanteFile) {
            message.error('El comprobante es obligatorio para pagos por transferencia');
            return;
          }
          comprobanteBase64 = await getBase64(comprobanteFile);
        }
      }

      const dataToSend = {
        alumnoId: alumnoParaCreditos.alu_id,
        planId: values.planId,
        tipoPagoId: tipoPagoId,
        comprobante: comprobanteBase64,
        metodoPago: metodoPago,
      };

      const response = await apiClient.post('/registro-pagos', dataToSend);
      if (response.ok) {
        const result = await response.json();
        message.success(`Créditos cargados correctamente. Se asignaron ${result.registro.plan_cantidad_creditos} créditos al alumno.`);
        formCreditos.resetFields();
        setComprobanteFile(null);
        // Recargar créditos y cambiar a la pestaña de ver
        if (alumnoParaCreditos) {
          await loadCreditosAlumno(alumnoParaCreditos.alu_id, 1, paginationCreditos.pageSize);
          setActiveTab('ver');
        }
        loadAlumnos(filtroNombre || null, filtroCategoria, pagination.current, pagination.pageSize);
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al cargar los créditos');
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.errorFields) {
        // Errores de validación del formulario
        return;
      }
      message.error('Error al cargar los créditos');
    }
  };

  // Columnas de la tabla
  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'alu_nombre',
      key: 'alu_nombre',
    },
    {
      title: 'Apellido',
      dataIndex: 'alu_apellido',
      key: 'alu_apellido',
    },
    {
      title: 'Email',
      dataIndex: 'alu_email',
      key: 'alu_email',
      render: (email) => email || '-',
    },
    {
      title: 'Teléfono',
      dataIndex: 'alu_telefono',
      key: 'alu_telefono',
      render: (telefono) => telefono || '-',
    },
    {
      title: 'Categoría',
      dataIndex: 'cat_descripcion',
      key: 'cat_descripcion',
      render: (descripcion) => descripcion || '-',
    },
    {
      title: 'Estado',
      dataIndex: 'alu_activo',
      key: 'alu_activo',
      width: 120,
      render: (activo) => (
        <Tag color={activo ? 'green' : 'red'}>
          {activo ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Button
            type="link"
            icon={<DollarOutlined />}
            onClick={() => handleCargarCreditos(record)}
            style={{ color: '#52c41a' }}
          >
            Créditos
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar este alumno?"
            onConfirm={() => handleDelete(record.alu_id)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>Administración de Alumnos</h1>
          <Space wrap style={{ marginBottom: 16, width: '100%' }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadAlumnos(filtroNombre || null, filtroCategoria, pagination.current, pagination.pageSize)}
              loading={loading}
            >
              {isMobile ? null : 'Actualizar'}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Nuevo Alumno
            </Button>
          </Space>
          
          {/* Filtros de búsqueda */}
          <div style={{ 
            marginBottom: 16, 
            padding: 16, 
            backgroundColor: '#2d2d2d', 
            borderRadius: 8,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 12,
            alignItems: isMobile ? 'stretch' : 'flex-end'
          }}>
            <div style={{ flex: 1, minWidth: isMobile ? '100%' : '200px' }}>
              <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                Buscar por nombre
              </label>
              <Input
                placeholder="Ej: valen, longo, valentin..."
                value={filtroNombre}
                onChange={handleFiltroNombreChange}
                allowClear
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ flex: 1, minWidth: isMobile ? '100%' : '200px' }}>
              <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                Filtrar por categoría
              </label>
              <Select
                placeholder="Todas las categorías"
                value={filtroCategoria}
                onChange={handleFiltroCategoriaChange}
                allowClear
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {categorias.map(cat => (
                  <Select.Option key={cat.cat_id} value={cat.cat_id} label={cat.cat_descripcion}>
                    {cat.cat_descripcion}
                  </Select.Option>
                ))}
              </Select>
            </div>
            {(filtroNombre || filtroCategoria) && (
              <Button
                onClick={handleLimpiarFiltros}
                style={{ minWidth: isMobile ? '100%' : 'auto' }}
              >
                Limpiar Filtros
              </Button>
            )}
          </div>
        </div>

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {alumnos.map(alumno => (
              <Card
                key={alumno.alu_id}
                hoverable
                style={{
                  borderLeft: '4px solid #1890ff',
                  borderRadius: '8px',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px', color: '#ffffff' }}>
                        {alumno.alu_nombre} {alumno.alu_apellido}
                      </div>
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                        Email: {alumno.alu_email || '-'}
                      </div>
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                        Teléfono: {alumno.alu_telefono || '-'}
                      </div>
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                        Categoría: {alumno.cat_descripcion || '-'}
                      </div>
                    </div>
                    <Tag color={alumno.alu_activo ? 'green' : 'red'}>
                      {alumno.alu_activo ? 'Activo' : 'Inactivo'}
                    </Tag>
                  </div>
                  <Space wrap>
                    <Button
                      type="link"
                      icon={<EditOutlined />}
                      onClick={() => handleEdit(alumno)}
                    >
                      Editar
                    </Button>
                    <Button
                      type="link"
                      icon={<DollarOutlined />}
                      onClick={() => handleCargarCreditos(alumno)}
                      style={{ color: '#52c41a' }}
                    >
                      Créditos
                    </Button>
                    <Popconfirm
                      title="¿Estás seguro de eliminar este alumno?"
                      onConfirm={() => handleDelete(alumno.alu_id)}
                      okText="Sí"
                      cancelText="No"
                    >
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                      >
                        Eliminar
                      </Button>
                    </Popconfirm>
                  </Space>
                </div>
              </Card>
            ))}
            {pagination.total > 0 && (
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  onChange={(page, pageSize) => {
                    setPagination(prev => ({ ...prev, current: page, pageSize }));
                    loadAlumnos(filtroNombre || null, filtroCategoria, page, pageSize);
                  }}
                  onShowSizeChange={(current, size) => {
                    setPagination(prev => ({ ...prev, current: 1, pageSize: size }));
                    loadAlumnos(filtroNombre || null, filtroCategoria, 1, size);
                  }}
                  showSizeChanger
                  pageSizeOptions={['10', '20', '50', '100']}
                  showTotal={(total) => `Total: ${total} alumnos`}
                />
              </div>
            )}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={alumnos}
            loading={loading}
            rowKey="alu_id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} alumnos`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
          />
        )}

        {/* Modal para crear/editar */}
        <Modal
          title={editingAlumno ? 'Editar Alumno' : 'Nuevo Alumno'}
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          okText="Guardar"
          cancelText="Cancelar"
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
          >
            <Form.Item
              name="nombre"
              label="Nombre"
              rules={[
                { required: true, message: 'El nombre es requerido' },
                { max: 100, message: 'El nombre no puede exceder 100 caracteres' },
              ]}
            >
              <Input placeholder="Ingrese el nombre" />
            </Form.Item>

            <Form.Item
              name="apellido"
              label="Apellido"
              rules={[
                { required: true, message: 'El apellido es requerido' },
                { max: 100, message: 'El apellido no puede exceder 100 caracteres' },
              ]}
            >
              <Input placeholder="Ingrese el apellido" />
            </Form.Item>

            <Form.Item
              name="fechaNacimiento"
              label="Fecha de Nacimiento"
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder="Seleccione la fecha de nacimiento"
              />
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.fechaNacimiento !== currentValues.fechaNacimiento
              }
            >
              {({ getFieldValue }) => {
                const fechaNacimiento = getFieldValue('fechaNacimiento');
                const edad = fechaNacimiento ? calcularEdad(fechaNacimiento) : null;
                return (
                  <Form.Item label="Edad">
                    <Input
                      disabled
                      value={edad !== null ? `${edad} años` : '-'}
                      placeholder="Se calculará automáticamente"
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Form.Item
              name="sexo"
              label="Sexo"
            >
              <Select
                placeholder="Seleccione el sexo"
                allowClear
              >
                <Select.Option value="Masculino">Masculino</Select.Option>
                <Select.Option value="Femenino">Femenino</Select.Option>
                <Select.Option value="Otro">Otro</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { type: 'email', message: 'El formato del email no es válido' },
              ]}
            >
              <Input placeholder="ejemplo@email.com" />
            </Form.Item>

            <Form.Item
              name="telefono"
              label="Teléfono"
            >
              <Input placeholder="Ingrese el teléfono" />
            </Form.Item>

            <Form.Item
              name="categoria"
              label="Categoría"
            >
              <Select
                placeholder="Seleccione la categoría"
                allowClear
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {categorias.map(cat => (
                  <Select.Option key={cat.cat_id} value={cat.cat_id} label={cat.cat_descripcion}>
                    {cat.cat_descripcion}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="activo"
              label="Estado"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
            </Form.Item>
          </Form>
        </Modal>

        {/* Modal para créditos con pestañas */}
        <Modal
          title={`Créditos - ${alumnoParaCreditos ? `${alumnoParaCreditos.alu_nombre} ${alumnoParaCreditos.alu_apellido}` : ''}`}
          open={modalCreditosVisible}
          onCancel={() => {
            setModalCreditosVisible(false);
            formCreditos.resetFields();
            setComprobanteFile(null);
            setCreditoGratuito(false);
            setAlumnoParaCreditos(null);
            setActiveTab('cargar');
            setCreditosAlumno([]);
            setPaginationCreditos({ current: 1, pageSize: 10, total: 0 });
            setTotalCreditosActivos(0);
          }}
          footer={activeTab === 'cargar' ? [
            <Button key="cancel" onClick={() => {
              setModalCreditosVisible(false);
              formCreditos.resetFields();
              setComprobanteFile(null);
              setCreditoGratuito(false);
              setAlumnoParaCreditos(null);
              setActiveTab('cargar');
              setCreditosAlumno([]);
              setPaginationCreditos({ current: 1, pageSize: 10, total: 0 });
              setTotalCreditosActivos(0);
            }}>
              Cancelar
            </Button>,
            <Button key="submit" type="primary" onClick={handleSaveCreditos}>
              Cargar Créditos
            </Button>,
          ] : [
            <Button key="close" onClick={() => {
              setModalCreditosVisible(false);
              formCreditos.resetFields();
              setComprobanteFile(null);
              setCreditoGratuito(false);
              setAlumnoParaCreditos(null);
              setActiveTab('cargar');
              setCreditosAlumno([]);
              setPaginationCreditos({ current: 1, pageSize: 10, total: 0 });
              setTotalCreditosActivos(0);
            }}>
              Cerrar
            </Button>,
          ]}
          width={800}
        >
          <Tabs activeKey={activeTab} onChange={handleTabChange}>
            <Tabs.TabPane tab="Cargar Créditos" key="cargar">
              <Form
                form={formCreditos}
                layout="vertical"
              >
                {esAdministrador() && (
                  <Form.Item
                    name="creditoGratuito"
                    valuePropName="checked"
                    initialValue={false}
                  >
                    <Switch
                      checked={creditoGratuito}
                      onChange={(checked) => {
                        setCreditoGratuito(checked);
                        if (checked) {
                          formCreditos.setFieldsValue({
                            planId: undefined,
                            cantidadCreditos: 1,
                            valor: 0,
                            diasValidez: 30,
                            metodoPago: undefined,
                          });
                        } else {
                          formCreditos.setFieldsValue({
                            cantidadCreditos: undefined,
                            valor: undefined,
                            diasValidez: undefined,
                          });
                        }
                      }}
                      checkedChildren="Crédito Gratuito"
                      unCheckedChildren="Plan Normal"
                    />
                    <div style={{ fontSize: '12px', color: '#b0b0b0', marginTop: 4 }}>
                      Activar para cargar 1 crédito gratuito (solo administradores)
                    </div>
                  </Form.Item>
                )}

                {creditoGratuito && esAdministrador() ? (
                  <>
                    <Form.Item
                      name="diasValidezGratuito"
                      label="Días de Validez"
                      initialValue={30}
                      rules={[
                        { required: true, message: 'Debe especificar los días de validez' },
                        { type: 'number', min: 1, message: 'Los días deben ser mayor a 0' },
                      ]}
                    >
                      <InputNumber
                        min={1}
                        placeholder="Ingrese los días de validez (ej: 30)"
                        style={{ width: '100%' }}
                        addonAfter="días"
                      />
                    </Form.Item>
                    <div style={{ 
                      padding: '12px', 
                      backgroundColor: '#1a1a1a', 
                      borderRadius: '4px',
                      marginBottom: '16px',
                      border: '1px solid #52c41a'
                    }}>
                      <div style={{ color: '#52c41a', fontWeight: 'bold', marginBottom: '4px' }}>
                        Crédito Gratuito
                      </div>
                      <div style={{ color: '#b0b0b0', fontSize: '13px' }}>
                        Se cargará 1 crédito gratuito con valor $0. No requiere método de pago.
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <Form.Item
                      name="planId"
                      label="Plan"
                      rules={[
                        { required: !creditoGratuito, message: 'Debe seleccionar un plan' },
                      ]}
                    >
                  <Select
                    placeholder="Seleccione un plan"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={(value) => {
                      const planSeleccionado = planes.find(p => p.pla_id === value);
                      if (planSeleccionado) {
                        formCreditos.setFieldsValue({
                          cantidadCreditos: planSeleccionado.pla_cantidad_creditos,
                          valor: planSeleccionado.pla_valor,
                          diasValidez: planSeleccionado.pla_dias_validez,
                        });
                      }
                    }}
                  >
                    {planes.map(plan => (
                      <Select.Option 
                        key={plan.pla_id} 
                        value={plan.pla_id} 
                        label={plan.pla_nombre}
                      >
                        {plan.pla_nombre} - ${plan.pla_valor} ({plan.pla_cantidad_creditos} créditos)
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="cantidadCreditos"
                  label="Cantidad de Créditos"
                >
                  <Input disabled placeholder="Se seleccionará automáticamente según el plan" />
                </Form.Item>

                <Form.Item
                  name="valor"
                  label="Valor del Plan"
                >
                  <Input disabled prefix="$" placeholder="Se seleccionará automáticamente según el plan" />
                </Form.Item>

                <Form.Item
                  name="diasValidez"
                  label="Días de Validez"
                >
                  <Input disabled placeholder="Se seleccionará automáticamente según el plan" />
                </Form.Item>

                    <Form.Item
                      name="metodoPago"
                      label="Método de Pago"
                      rules={[
                        { required: !creditoGratuito, message: 'Debe seleccionar un método de pago' },
                      ]}
                    >
                  <Select
                    placeholder="Seleccione el método de pago"
                    onChange={(value) => {
                      // Limpiar campos relacionados cuando cambia el método
                      if (value === 'PENDIENTE') {
                        formCreditos.setFieldsValue({ tipoPagoId: null, comprobante: null });
                        setComprobanteFile(null);
                      } else {
                        // Si es EFECTIVO o TRANSFERENCIA, establecer el tipoPagoId y limpiar comprobante
                        const tipoPago = tiposPago.find(tp => tp.tip_nombre === value);
                        if (tipoPago) {
                          formCreditos.setFieldsValue({ tipoPagoId: tipoPago.tip_id, comprobante: null });
                        }
                        setComprobanteFile(null);
                      }
                    }}
                  >
                    <Select.Option value="PENDIENTE">PENDIENTE</Select.Option>
                    {tiposPago.map(tipo => (
                      <Select.Option key={tipo.tip_id} value={tipo.tip_nombre}>
                        {tipo.tip_nombre}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) => 
                    prevValues.metodoPago !== currentValues.metodoPago
                  }
                >
                  {({ getFieldValue }) => {
                    const metodoPago = getFieldValue('metodoPago');
                    const tipoPago = tiposPago.find(tp => tp.tip_nombre === metodoPago);
                    const mostrarComprobante = tipoPago && tipoPago.tip_nombre === 'TRANSFERENCIA';
                    
                    return (
                      <>
                        {mostrarComprobante && (
                          <Form.Item
                            name="comprobante"
                            label="Comprobante de Transferencia"
                            rules={[
                              { required: true, message: 'Debe cargar el comprobante de transferencia' },
                            ]}
                          >
                            <Upload
                              accept="image/*,application/pdf"
                              beforeUpload={async (file) => {
                                try {
                                  // Comprimir el archivo
                                  const compressedFile = await compressFile(file);
                                  setComprobanteFile(compressedFile);
                                  // Actualizar el campo del formulario para que la validación funcione
                                  formCreditos.setFieldsValue({ comprobante: compressedFile.name });
                                  return false; // Prevent upload
                                } catch (error) {
                                  message.error(error.message || 'Error al procesar el archivo');
                                  return false;
                                }
                              }}
                              onRemove={() => {
                                setComprobanteFile(null);
                                formCreditos.setFieldsValue({ comprobante: null });
                              }}
                              maxCount={1}
                            >
                              <Button icon={<UploadOutlined />}>Seleccionar Archivo (Imagen o PDF)</Button>
                            </Upload>
                            <div style={{ fontSize: '12px', color: '#b0b0b0', marginTop: 4 }}>
                              Formatos aceptados: JPG, PNG, PDF (máx. 5MB)
                            </div>
                          </Form.Item>
                        )}
                      </>
                    );
                  }}
                </Form.Item>
                  </>
                )}
              </Form>
            </Tabs.TabPane>
            
            <Tabs.TabPane tab="Ver Créditos Actuales" key="ver">
              <div style={{ marginBottom: 16 }}>
                <Statistic
                  title="Créditos Activos"
                  value={totalCreditosActivos}
                  valueStyle={{ color: '#3f8600' }}
                  suffix="créditos"
                />
              </div>
              
              {isMobile ? (
                <div>
                  {creditosAlumno.map((credito) => (
                    <Card
                      key={credito.cre_id}
                      style={{ marginBottom: 16 }}
                      size="small"
                    >
                      <div style={{ marginBottom: 8 }}>
                        <strong>Plan:</strong> {credito.plan_nombre}
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <strong>Fecha Adquisición:</strong>{' '}
                        {dayjs(credito.cre_fecha_adquisicion).format('DD/MM/YYYY')}
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <strong>Fecha Vencimiento:</strong>{' '}
                        {dayjs(credito.cre_fecha_vencimiento).format('DD/MM/YYYY')}
                      </div>
                      <div>
                        <strong>Estado:</strong>{' '}
                        <Tag color={credito.cre_utilizado ? 'red' : (dayjs(credito.cre_fecha_vencimiento).isAfter(dayjs()) ? 'green' : 'orange')}>
                          {credito.cre_utilizado ? 'Utilizado' : (dayjs(credito.cre_fecha_vencimiento).isAfter(dayjs()) ? 'Activo' : 'Vencido')}
                        </Tag>
                      </div>
                      {credito.cre_utilizado && credito.cre_fecha_utilizacion && (
                        <div style={{ marginTop: 8 }}>
                          <strong>Fecha Utilización:</strong>{' '}
                          {dayjs(credito.cre_fecha_utilizacion).format('DD/MM/YYYY HH:mm')}
                        </div>
                      )}
                    </Card>
                  ))}
                  
                  <Pagination
                    current={paginationCreditos.current}
                    pageSize={paginationCreditos.pageSize}
                    total={paginationCreditos.total}
                    showSizeChanger
                    showTotal={(total) => `Total: ${total} créditos`}
                    pageSizeOptions={['10', '20', '50']}
                    onChange={(page, pageSize) => {
                      handleCreditosTableChange({ current: page, pageSize });
                    }}
                    onShowSizeChange={(current, size) => {
                      handleCreditosTableChange({ current: 1, pageSize: size });
                    }}
                    style={{ marginTop: 16, textAlign: 'center' }}
                  />
                </div>
              ) : (
                <Table
                  columns={[
                    {
                      title: 'Plan',
                      dataIndex: 'plan_nombre',
                      key: 'plan_nombre',
                    },
                    {
                      title: 'Fecha Adquisición',
                      dataIndex: 'cre_fecha_adquisicion',
                      key: 'cre_fecha_adquisicion',
                      render: (text) => dayjs(text).format('DD/MM/YYYY'),
                    },
                    {
                      title: 'Fecha Vencimiento',
                      dataIndex: 'cre_fecha_vencimiento',
                      key: 'cre_fecha_vencimiento',
                      render: (text) => dayjs(text).format('DD/MM/YYYY'),
                    },
                    {
                      title: 'Estado',
                      key: 'estado',
                      render: (_, record) => {
                        const isVencido = dayjs(record.cre_fecha_vencimiento).isBefore(dayjs());
                        if (record.cre_utilizado) {
                          return <Tag color="red">Utilizado</Tag>;
                        } else if (isVencido) {
                          return <Tag color="orange">Vencido</Tag>;
                        } else {
                          return <Tag color="green">Activo</Tag>;
                        }
                      },
                    },
                    {
                      title: 'Fecha Utilización',
                      dataIndex: 'cre_fecha_utilizacion',
                      key: 'cre_fecha_utilizacion',
                      render: (text) => text ? dayjs(text).format('DD/MM/YYYY HH:mm') : '-',
                    },
                  ]}
                  dataSource={creditosAlumno}
                  loading={loadingCreditos}
                  rowKey="cre_id"
                  pagination={{
                    current: paginationCreditos.current,
                    pageSize: paginationCreditos.pageSize,
                    total: paginationCreditos.total,
                    showSizeChanger: true,
                    showTotal: (total) => `Total: ${total} créditos`,
                    pageSizeOptions: ['10', '20', '50', '100'],
                  }}
                  onChange={handleCreditosTableChange}
                />
              )}
            </Tabs.TabPane>
          </Tabs>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default AdministracionAlumnos;

